/**
 * Screenshot Cropper Component
 * Allows users to capture a screenshot and crop a specific area for feedback
 */

import React, { useState, useCallback, useRef } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { cn } from '../../lib/utils';
import { captureScreenshot } from '../../utils/screenshot';

interface ScreenshotCropperProps {
  isActive: boolean;
  onAreaSelected: (croppedImage: string, cropData: CropData) => void;
  onCancel: () => void;
  title?: string;
  description?: string;
}

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  fullScreenshot: string;
  croppedImage: string;
}

const ScreenshotCropper: React.FC<ScreenshotCropperProps> = ({
  isActive,
  onAreaSelected,
  onCancel,
  title = "Select Area for Feedback",
  description = "Click and drag to select the area you want to comment on"
}) => {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Capture screenshot when component becomes active
  React.useEffect(() => {
    if (isActive && !screenshot) {
      captureFullPageScreenshot();
    }
  }, [isActive, screenshot]);

  const captureFullPageScreenshot = async () => {
    setIsCapturing(true);
    try {
      // Hide feedback UI elements before capture
      const feedbackElements = document.querySelectorAll('[data-feedback-widget], [data-feedback-modal]');
      feedbackElements.forEach(el => {
        (el as HTMLElement).style.display = 'none';
      });

      // Capture the screenshot
      const result = await captureScreenshot({
        quality: 0.95,
        format: 'png'
      });

      // Show feedback elements again
      feedbackElements.forEach(el => {
        (el as HTMLElement).style.display = '';
      });

      setScreenshot(result.fullImage);
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      onCancel();
    } finally {
      setIsCapturing(false);
    }
  };

  const getCroppedImage = useCallback(async (): Promise<string> => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return '';
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return canvas.toDataURL('image/png');
  }, [completedCrop]);

  const handleConfirm = async () => {
    if (!completedCrop || !screenshot) return;

    try {
      const croppedImage = await getCroppedImage();
      
      const cropData: CropData = {
        x: completedCrop.x,
        y: completedCrop.y,
        width: completedCrop.width,
        height: completedCrop.height,
        fullScreenshot: screenshot,
        croppedImage
      };

      onAreaSelected(croppedImage, cropData);
    } catch (error) {
      console.error('Failed to process crop:', error);
    }
  };

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive) return;
      
      if (e.key === 'Enter' && completedCrop) {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, completedCrop, handleConfirm, onCancel]);

  const handleReset = () => {
    setScreenshot(null);
    setCrop(undefined);
    setCompletedCrop(null);
    captureFullPageScreenshot();
  };

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-[10000000] bg-black bg-opacity-90 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div>
            <h2 className="text-lg md:text-xl font-serif font-medium text-slate-800">
              {title}
            </h2>
            <p className="text-xs md:text-sm text-slate-600 mt-1">
              {description}
            </p>
            {/* Mobile-specific guidance */}
            <div className="md:hidden mt-2 text-xs text-slate-500">
              Pinch to zoom • Drag to select area
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={handleReset}
              className="px-3 md:px-4 py-2 min-h-[44px] text-slate-600 hover:text-slate-800 transition-colors touch-manipulation"
              disabled={isCapturing}
              aria-label="Recapture screenshot"
            >
              <span className="hidden md:inline">Recapture</span>
              <svg className="w-5 h-5 md:hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 4v6h6"/>
                <path d="M23 20v-6h-6"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
              </svg>
            </button>
            <button
              onClick={onCancel}
              className="px-3 md:px-4 py-2 min-h-[44px] text-slate-600 hover:text-slate-800 transition-colors touch-manipulation"
              aria-label="Cancel screenshot selection"
            >
              <span className="hidden md:inline">Cancel</span>
              <svg className="w-5 h-5 md:hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18"/>
                <path d="M6 6l12 12"/>
              </svg>
            </button>
            <button
              onClick={handleConfirm}
              disabled={!completedCrop || isCapturing}
              className={cn(
                "px-4 md:px-6 py-2 min-h-[44px] bg-warm-500 text-white font-medium transition-all touch-manipulation",
                "hover:bg-warm-600 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed",
                "shadow-sm"
              )}
              aria-label="Confirm area selection"
            >
              <span className="hidden md:inline">Confirm Selection</span>
              <span className="md:hidden">Confirm</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          {isCapturing ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-white">Capturing screenshot...</p>
              </div>
            </div>
          ) : screenshot ? (
            <div className="relative bg-white p-4 rounded-lg shadow-xl">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={undefined}
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  src={screenshot}
                  alt="Screenshot for cropping"
                  className="max-w-full h-auto"
                  style={{ maxHeight: '70vh' }}
                />
              </ReactCrop>
              
              {/* Hidden canvas for generating cropped image */}
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-96">
              <p className="text-white">Failed to capture screenshot</p>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-slate-800 text-white px-6 py-3 text-center">
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-6 text-sm">
          <span className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="5" y="5" width="14" height="14" rx="2"/>
              <path d="M9 9h6v6h-6z"/>
            </svg>
            Click and drag to select area
          </span>
          <span className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12h16m-8-8v16"/>
            </svg>
            Resize by dragging corners
          </span>
          <span className="flex items-center gap-2">
            <kbd className="px-2 py-0.5 bg-slate-700 rounded text-xs">ESC</kbd>
            Cancel selection
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScreenshotCropper;